'use client';

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/common/dialog';
import { Button } from '@/components/common/Button';
import Tooltip from '@/components/common/Tooltip';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Sparkles } from 'lucide-react';
import { ImageGenerationForm } from './image-selection/ImageGenerationForm';
import { SuggestionsGrid } from './image-selection/SuggestionsGrid';
import { apiClient } from '@/app/api/client';
import { getPublicAssetUrl } from '@/utils/assetUrl';
import ConfirmDialog from '@/components/common/ConfirmDialog';

// ============================================
// 🚧 DEV MODE - SET TO false BEFORE PRODUCTION
// ============================================
const DEV_MODE = false; // Skip matching, go straight to generation
// ============================================

interface AIGenerateImageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (params: Record<string, any>) => Promise<string>;
  initialContent?: string;
  selectedText?: string;
  onAccept?: (content: string) => void;
  aiService?: any; // AICommandService instance
  articleId?: string; // Article ID for fetching full content
  articleTitle?: string; // Article title for context
}

// Types for image context and selection
interface ImageContext {
  articleId?: string;
  articleTitle: string;
  currentParagraph: string;
  selectedText?: string;
  cursorPosition?: number;
  scenario?: 'hero' | 'contextual';
}

interface SelectedImage {
  url: string;
  alt: string;
  assetId?: string;
}

// Wizard steps
type WizardStep = 'intention' | 'processing' | 'matches' | 'generate';

// Intention options
type IntentionType = 'hero' | 'contextual' | 'custom' | 'browse';

export default function AIGenerateImageDialog({
  isOpen,
  onClose,
  onSubmit,
  initialContent = '',
  selectedText = '',
  onAccept,
  aiService,
  articleId,
  articleTitle
}: AIGenerateImageDialogProps) {
  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('intention');
  const [intentionType, setIntentionType] = useState<IntentionType>('contextual');
  const [customIntention, setCustomIntention] = useState('');
  const [showMatches, setShowMatches] = useState(false);
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [hasMatches, setHasMatches] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [pendingAction, setPendingAction] = useState<'close' | 'back' | null>(null);

  // Context data
  const [context, setContext] = useState<ImageContext>({
    articleTitle: '',
    currentParagraph: '',
    selectedText: '',
    cursorPosition: 0,
    scenario: 'contextual'
  });

  // Generate smart preset based on intention type and context
  const generatePresetDirections = (type: IntentionType): string => {
    if (type === 'hero') {
      return 'Generate a nice hero or banner image for this article';
    }

    if (type === 'browse') {
      return 'Find relevant images from my assets';
    }

    // For contextual/illustrate content
    if (context.selectedText) {
      return 'Generate a nice illustrating picture for the selected content';
    }

    if (context.currentParagraph) {
      // Check if it's a heading (starts with #)
      if (context.currentParagraph.startsWith('#')) {
        return 'Generate a nice illustrating picture for this section';
      }
      return 'Generate a nice illustrating picture for the current paragraph';
    }

    return '';
  };

  // Update custom intention when intention type changes
  useEffect(() => {
    const preset = generatePresetDirections(intentionType);
    setCustomIntention(preset);
  }, [intentionType, context.selectedText, context.currentParagraph]);

  // Reset and gather context when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Reset wizard state
      setCurrentStep('intention');
      setShowMatches(false);
      setSelectedImage(null);

      // Set initial intention type based on context
      const initialType: IntentionType = context.selectedText || context.currentParagraph ? 'contextual' : 'hero';
      setIntentionType(initialType);

      // Don't reset customIntention here - let the useEffect above handle it

      // Get context data from AICommandService if available
      if (aiService) {
        try {
          // Try to get current paragraph text
          const currentParagraph = aiService.getCurrentParagraph();
          let currentParagraphText = currentParagraph?.text || '';

          // If no paragraph (e.g., cursor on heading), try to get heading text
          if (!currentParagraphText && aiService.getCurrentHeading) {
            const currentHeading = aiService.getCurrentHeading();
            currentParagraphText = currentHeading?.node?.textContent || '';
          }

          // Get cursor position
          const cursorPos = aiService.editor?.state.selection.from || 0;

          // Smart scenario detection for default intention
          const scenario = determineScenario(selectedText, cursorPos);
          setIntentionType(scenario === 'hero' ? 'hero' : 'contextual');

          setContext({
            articleId: articleId,
            articleTitle: articleTitle || 'Untitled Article',
            currentParagraph: currentParagraphText.substring(0, 500),
            selectedText: selectedText || '',
            cursorPosition: cursorPos,
            scenario
          });
        } catch (error) {
          console.error('Error getting context data:', error);
          setContext({
            articleId: articleId,
            articleTitle: articleTitle || 'Untitled Article',
            currentParagraph: selectedText || '',
            selectedText: selectedText || '',
            cursorPosition: 0,
            scenario: 'contextual'
          });
          setIntentionType('contextual');
        }
      }
    }
  }, [isOpen, aiService, selectedText]);

  // Smart scenario detection
  const determineScenario = (selected: string, cursorPos: number): 'hero' | 'contextual' => {
    if (selected) {
      return 'contextual'; // User selected text → illustrate this
    }
    if (cursorPos < 200) {
      return 'hero'; // Near top → hero image
    }
    return 'contextual'; // Default → contextual
  };

  // Handle Next button from intention step
  const handleNext = async () => {
    // 🚧 DEV MODE: Skip matching, go straight to generation
    if (DEV_MODE) {
      setCurrentStep('generate');
      return;
    }

    setCurrentStep('processing');

    // Build user message from intention type and optional directions
    let userMessage = intentionType === 'hero'
      ? 'Generate hero image for this article'
      : 'Generate illustrating image for selected content';

    // Append additional directions if provided
    if (customIntention.trim()) {
      userMessage += `. Additional directions: ${customIntention.trim()}`;
    }

    // Move to matches step and show loading
    setIsLoadingMatches(true);
    setShowMatches(true);
    setCurrentStep('matches');
  };

  // Handle back button
  const handleBack = () => {
    if (isGenerating) {
      setPendingAction('back');
      setShowConfirmClose(true);
      return;
    }
    if (currentStep === 'matches' || currentStep === 'generate') {
      setCurrentStep('intention');
      setSelectedImage(null);
    }
  };

  // Handle image click (selection, not insertion)
  const handleImageClick = (image: SelectedImage) => {
    setSelectedImage(image);
  };

  // Handle use selected button
  const handleUseSelected = () => {
    if (selectedImage) {
      handleImageSelect(selectedImage);
    }
  };

  // Handle switch to generate from matches
  const handleSwitchToGenerate = () => {
    setCurrentStep('generate');
  };

  // Handle image selection from suggestions
  const handleImageSelect = (image: SelectedImage) => {
    console.log('🖼️ AIGenerateImageDialog: Image selected', {
      url: image.url,
      alt: image.alt,
      assetId: image.assetId
    });
    if (onAccept) {
      // Generate public URL for the asset
      const publicUrl = image.assetId
        ? getPublicAssetUrl(image.assetId, 'image')
        : image.url;

      console.log('🖼️ AIGenerateImageDialog: Using public URL', publicUrl);

      // Return image node object for proper insertion
      const imageNode = JSON.stringify({
        type: 'image',
        attrs: {
          src: publicUrl,
          alt: image.alt || 'AI-generated image'
        }
      });
      console.log('🖼️ AIGenerateImageDialog: Sending image node', imageNode);
      onAccept(imageNode);
    }
    onClose();
  };

  // Handle image generation completion
  const handleImageGenerated = (image: SelectedImage) => {
    if (onAccept) {
      // Generate public URL for the asset
      const publicUrl = image.assetId
        ? getPublicAssetUrl(image.assetId, 'image')
        : image.url;

      console.log('🖼️ AIGenerateImageDialog: Generated image public URL', publicUrl);

      // Return image node object for proper insertion
      const imageNode = JSON.stringify({
        type: 'image',
        attrs: {
          src: publicUrl,
          alt: image.alt || 'AI-generated image'
        }
      });
      onAccept(imageNode);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="sm:max-w-[900px] max-h-[85vh] p-0 overflow-hidden"
        hideCloseButton={true}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="p-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-semibold">
                {intentionType === 'browse' ? 'Browse Assets' : 'AI Image Selection'}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 mt-1">
                {intentionType === 'browse'
                  ? 'Select from your existing assets'
                  : 'Find existing images or generate new ones with AI'}
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => {
                if (isGenerating) {
                  setPendingAction('close');
                  setShowConfirmClose(true);
                } else {
                  onClose();
                }
              }}
            >
              <XMarkIcon className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="p-6 max-h-[calc(85vh-120px)] overflow-y-auto">
          {/* Step 1: Intention Selection */}
          {currentStep === 'intention' && (
            <div className="space-y-4">
              {/* Context Label */}
              <h4 className="text-sm font-semibold text-gray-900">Context</h4>

              {/* Context Display */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-2.5 max-w-full">
                {/* Article Title */}
                <div className="grid grid-cols-[auto_1fr] gap-2 items-start max-w-full">
                  <span className="text-xs font-medium text-gray-600">Article:</span>
                  <div className="min-w-0 overflow-hidden">
                    <Tooltip content={context.articleTitle || 'Untitled Article'}>
                      <span className="text-xs text-gray-900 block truncate cursor-pointer">
                        {context.articleTitle || 'Untitled Article'}
                      </span>
                    </Tooltip>
                  </div>
                </div>

                {/* Current Location (Heading or Paragraph) */}
                <div className="grid grid-cols-[auto_1fr] gap-2 items-start max-w-full">
                  <span className="text-xs font-medium text-gray-600 leading-tight">
                    Cursor on {context.currentParagraph?.startsWith('#') ? 'heading' : 'paragraph'}:
                  </span>
                  <div className="min-w-0 overflow-hidden">
                    <Tooltip content={context.currentParagraph || 'Empty paragraph'}>
                      <span className="text-xs text-gray-700 block truncate cursor-pointer">
                        {context.currentParagraph || '(empty paragraph)'}
                      </span>
                    </Tooltip>
                  </div>
                </div>

                {/* Selected Text (if any) */}
                {context.selectedText && (
                  <div className="grid grid-cols-[auto_1fr] gap-2 items-start max-w-full">
                    <span className="text-xs font-medium text-gray-600">Selected content:</span>
                    <div className="min-w-0 overflow-hidden">
                      <Tooltip content={context.selectedText}>
                        <span className="text-xs text-blue-700 block truncate font-medium cursor-pointer">
                          {context.selectedText}
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                )}
              </div>

              {/* Image Type Selection - Inline Radio Buttons */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-900">
                  What kind of image do you need?
                </label>
                <div className="flex gap-3">
                  {/* Hero Image Option */}
                  <label className={`flex-1 flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-all ${intentionType === 'hero' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                    }`}>
                    <input
                      type="radio"
                      name="intention"
                      value="hero"
                      checked={intentionType === 'hero'}
                      onChange={() => {
                        setIntentionType('hero');
                      }}
                      className="mr-2"
                    />
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-900">🎯 Hero Image</div>
                      <div className="text-xs text-gray-600 mt-0.5">For article opening</div>
                    </div>
                  </label>

                  {/* Contextual Image Option */}
                  <label className={`flex-1 flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-all ${intentionType === 'contextual' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                    }`}>
                    <input
                      type="radio"
                      name="intention"
                      value="contextual"
                      checked={intentionType === 'contextual'}
                      onChange={() => {
                        setIntentionType('contextual');
                      }}
                      className="mr-2"
                    />
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-900">📍 Illustrate Content</div>
                      <div className="text-xs text-gray-600 mt-0.5">For this section</div>
                    </div>
                  </label>

                  {/* Browse Assets Option */}
                  <label className={`flex-1 flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-all ${intentionType === 'browse' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                    }`}>
                    <input
                      type="radio"
                      name="intention"
                      value="browse"
                      checked={intentionType === 'browse'}
                      onChange={() => {
                        setIntentionType('browse');
                      }}
                      className="mr-2"
                    />
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-900">📂 Browse Assets</div>
                      <div className="text-xs text-gray-600 mt-0.5">From library</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Optional Directions */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-900">
                  Additional directions <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={customIntention}
                  onChange={(e) => setCustomIntention(e.target.value)}
                  placeholder='e.g., "workflow diagram", "screenshot style", "minimalist illustration"...'
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-gray-900"
                  rows={3}
                />
                <p className="text-xs text-gray-500">
                  Provide specific requirements or style preferences to help AI find or generate the perfect image
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4 border-t">
                {/* Left: Generate Now Shortcut - Hidden for Browse mode */}
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>

                {/* Right: Cancel & Next */}
                <div className="flex gap-3">
                  {intentionType !== 'browse' ? (
                    <Button
                      variant="ghost"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      onClick={handleSwitchToGenerate}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Now
                    </Button>
                  ) : <div></div>}
                  <Button
                    onClick={handleNext}
                    className="flex items-center gap-2"
                  >
                    Next →
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Processing */}
          {currentStep === 'processing' && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-6"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Searching for images...</h3>
              <p className="text-sm text-gray-600 text-center max-w-md">
                {intentionType === 'hero'
                  ? 'Looking for hero images that match your article'
                  : intentionType === 'contextual'
                    ? 'Finding images that illustrate your content'
                    : intentionType === 'browse'
                      ? 'Loading your asset library'
                      : `Searching for: "${customIntention}"`
                }
              </p>
            </div>
          )}

          {/* Step 3: Matches Found */}
          {currentStep === 'matches' && showMatches && (
            <div className="space-y-4">
              <SuggestionsGrid
                context={context}
                articleId={articleId}
                userMessage={customIntention.trim() || undefined}
                onSelect={handleImageSelect}
                onNoMatches={() => {
                  setIsLoadingMatches(false);
                  setHasMatches(false);
                  // Only auto-switch to generate if we are NOT browsing
                  // If browsing, we want to show the "No assets found" state in the grid
                  if (intentionType !== 'browse') {
                    setCurrentStep('generate');
                  }
                }}
                onHasMatches={() => {
                  setIsLoadingMatches(false);
                  setHasMatches(true);
                }}
                onSwitchToGenerate={handleSwitchToGenerate}
                selectedImageId={selectedImage?.assetId}
                onImageClick={handleImageClick}
                mode={intentionType === 'browse' ? 'browse' : 'match'}
              />

              {/* Footer with action buttons */}
              <div className="flex justify-between items-center pt-4 border-t">
                <Button variant="outline" onClick={handleBack}>
                  ← Back
                </Button>

                {/* Show different buttons based on loading state */}
                {isLoadingMatches ? (
                  // While loading: Show "Generate Now" to skip matching
                  <Button onClick={handleSwitchToGenerate}>
                    Generate Now →
                  </Button>
                ) : !hasMatches ? (
                  null
                ) : (
                  // After loading: Show normal buttons
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleSwitchToGenerate}>
                      None of these? Generate New
                    </Button>
                    <Button
                      onClick={handleUseSelected}
                      disabled={!selectedImage}
                      className="min-w-[140px]"
                    >
                      Use Selected
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Generate New */}
          {currentStep === 'generate' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Generate New Image</h3>

              <ImageGenerationForm
                context={context}
                userMessage={customIntention}
                onGenerated={handleImageGenerated}
                onClose={() => {
                  if (isGenerating) {
                    setPendingAction('close');
                    setShowConfirmClose(true);
                  } else {
                    onClose();
                  }
                }}
                onBack={handleBack}
                isGenerating={isGenerating}
                onIsGeneratingChange={setIsGenerating}
              />
            </div>
          )}
        </div>

        <ConfirmDialog
          isOpen={showConfirmClose}
          onClose={() => {
            setShowConfirmClose(false);
            setPendingAction(null);
          }}
          onConfirm={() => {
            if (pendingAction === 'close') {
              onClose();
            } else if (pendingAction === 'back') {
              setIsGenerating(false); // Cancel generation state
              setCurrentStep('intention');
              setSelectedImage(null);
            }
          }}
          title="Stop Generating?"
          message="Image generation is in progress. If you leave now, the process will be cancelled."
          confirmText="Yes, Stop Generation"
          confirmButtonClassName="bg-red-600 text-white hover:bg-red-700"
        />
      </DialogContent>
    </Dialog>
  );
}
